import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../lib/api";
import API from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gem, Eye, EyeOff, Wallet, RefreshCw } from "lucide-react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

export default function RegisterPage() {
  const { register } = useAuth();
  const { siteTitle } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [tab, setTab] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fpAddress, setFpAddress] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fpEnabled, setFpEnabled] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  useEffect(() => {
    API.get("/faucetpay/status").then(r => setFpEnabled(r.data.register_enabled)).catch(() => {});
  }, []);

  const handleEmail = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/register", { email, password, name, ref_code: refCode || undefined });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      if (data.email_verification_required) {
        setRegisteredEmail(email);
        setVerificationSent(true);
      } else {
        window.location.href = "/dashboard";
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleFaucetPay = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/faucetpay", { address: fpAddress });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      window.location.href = "/dashboard";
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg("");
    try {
      const { data } = await API.post("/auth/send-verification");
      setResendMsg(data.message || "Verification email resent!");
    } catch {
      setResendMsg("Failed to resend. Please try again.");
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <span className="text-2xl font-bold text-[#F8FAFC]">{siteTitle}</span>
          </Link>
          <h1 className="text-2xl font-bold text-[#F8FAFC] mb-2">Create Account</h1>
          <p className="text-sm text-[#94A3B8]">Start earning crypto rewards</p>
        </div>

        {verificationSent ? (
          <div className="bg-[#131722] rounded-xl border border-[#10B981]/30 p-8 text-center" data-testid="verification-sent">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
              <Gem className="w-8 h-8 text-[#10B981]" />
            </div>
            <h2 className="text-xl font-bold text-[#F8FAFC] mb-2">Check Your Email</h2>
            <p className="text-sm text-[#94A3B8] mb-4">We sent a verification link to <span className="text-[#00F0FF]">{registeredEmail}</span></p>
            <p className="text-xs text-[#64748B] mb-6">Click the link in the email to verify your account, then come back and log in.</p>
            <Link to="/login" className="inline-block px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90" data-testid="go-to-login-btn">
              Go to Login
            </Link>
            {/* Resend verification */}
            <div className="mt-6 pt-4 border-t border-[#2A3241]/30">
              <p className="text-xs text-[#64748B] mb-3">Didn't receive the email?</p>
              <button onClick={handleResend} disabled={resending} data-testid="resend-verification-btn"
                className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#94A3B8] border border-[#2A3241] rounded-lg hover:text-[#F8FAFC] hover:border-[#00F0FF]/50 transition-all disabled:opacity-50 inline-flex items-center gap-2">
                <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
                {resending ? "Sending..." : "Resend Verification Email"}
              </button>
              {resendMsg && <p className="text-xs text-[#10B981] mt-2" data-testid="resend-msg">{resendMsg}</p>}
            </div>
          </div>
        ) : (<>

        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40 mb-6">
          <button onClick={() => setTab("email")} data-testid="tab-email"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === "email" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
            Email Register
          </button>
          {fpEnabled && (
            <button onClick={() => setTab("faucetpay")} data-testid="tab-faucetpay"
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all flex items-center justify-center gap-1.5 ${tab === "faucetpay" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              <Wallet className="w-3.5 h-3.5" /> FaucetPay
            </button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm text-[#EF4444] mb-4" data-testid="register-error">{error}</div>
        )}

        {tab === "email" ? (
          <form onSubmit={handleEmail} className="bg-[#131722] rounded-xl border border-[#2A3241] p-8 space-y-5" data-testid="register-form">
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your player name" required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#00F0FF]"
                data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@example.com" required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#00F0FF]"
                data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">Password</Label>
              <div className="relative">
                <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#00F0FF] pr-10"
                  data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(0,240,255,0.4)]">
              {loading ? "Creating..." : "Start Earning"}
            </button>
            <p className="text-center text-sm text-[#94A3B8]">
              Already have an account? <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="register-login-link">Login</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleFaucetPay} className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-8 space-y-5" data-testid="faucetpay-register-form">
            <div className="text-center mb-2">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FFD600]/10 flex items-center justify-center mb-3">
                <Wallet className="w-7 h-7 text-[#FFD600]" />
              </div>
              <p className="text-sm text-[#94A3B8]">Instant registration with your FaucetPay wallet</p>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm mb-2 block">FaucetPay Crypto Address</Label>
              <Input value={fpAddress} onChange={(e) => setFpAddress(e.target.value)} placeholder="Your BTC, LTC, or other crypto address" required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#FFD600]"
                data-testid="faucetpay-address-input" />
              <p className="text-xs text-[#64748B] mt-2">Address must be linked to a FaucetPay account</p>
            </div>
            <button type="submit" disabled={loading} data-testid="faucetpay-register-btn"
              className="w-full py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 transition-all disabled:opacity-50 shadow-[0_0_15px_rgba(255,214,0,0.4)]">
              {loading ? "Verifying..." : "Register with FaucetPay"}
            </button>
          </form>
        )}
        </>)}
      </div>
    </div>
  );
}
